import React from 'react';

const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
    {children}
  </span>
);

const experiences = [
  {
    period: 'Jun 2024 – Present',
    title: 'SENIOR FRONTEND DEVELOPER',
    company: 'Valtech',
    location: 'Lisboa, Portugal',
    summary:
      'Part of a cross-functional team building performant, scalable storefronts with Angular + SAP Composable Storefront.',
    impact: [
      'Led complex UI modules used across global markets; consistent UX and performance at scale.',
      'Integrated Keycloak SSO, Coveo AI search and Medallia Feedback SDK to unlock personalization & analytics.',
      'Designed reusable components (status pills, product cards, modals, consent dialogs) aligned to design system and WCAG 2.1.',
      'Championed a11y & responsive quality: ARIA patterns, keyboard flows, truncation/grid utilities for multilingual layouts.',
      'Optimized NgRx state, OCC API comms and feature modularization with clean selectors/effects & error handling.',
      'Knowledge sharing & mentoring: led PT Frontend Craft Circle; helped run Frontend Day and Tech Girl initiatives.',
      'Drove code review and performance audits for a robust, multi-tenant architecture (Core Web Vitals guardrails).',
    ],
    skills: [
      'Angular',
      'SAP Composable Storefront (Spartacus)',
      'NgRx',
      'Keycloak',
      'Coveo',
      'Medallia',
      'Contentful',
      'CI/CD',
      'Core Web Vitals',
      'A11y',
      'TypeScript',
      'RxJS',
      'Azure DevOps',
      'Docker',
    ],
  },
  {
    period: 'Aug 2022 – Jun 2024',
    title: 'INTERMEDIATE FRONTEND DEVELOPER',
    company: 'Valtech',
    location: 'Lisboa, Portugal',
    summary:
      'Delivered features for enterprise e-commerce (Angular) and integrated headless CMS. Supported delivery with CI/CD and testing.',
    impact: [
      'Built dynamic, reusable UI with responsive behavior and unit tests.',
      'Integrated Contentful and built safe authoring guardrails & previews.',
      'Containerized projects for consistent local DX; contributed to CI pipelines and deployment workflows.',
      'Mentored juniors (young talents program, Tech Girl); regular internal talks and FE session events.',
      'Introduced perf & quality checks (lint, Sonar, coverage thresholds) to keep releases stable.',
    ],
    skills: [
      'Angular',
      'Contentful',
      'TypeScript',
      'RxJS',
      'SCSS',
      'Unit Testing',
      'Sonar',
      'Docker',
      'CI/CD',
      'PWA',
    ],
  },
  {
    period: '2018 – 2022',
    title: 'FULL STACK DEVELOPER',
    company: 'Jumia Porto Tech Center',
    location: 'Porto, Portugal',
    summary:
      'Worked on order/warehouse management and a finance/payments web app within an e-commerce ecosystem.',
    impact: [
      'Implemented BE services & FE features; improved data flows between Java/Spring Boot and Angular frontends.',
      'Introduced monitoring and dashboards (Grafana) for key flows; helped reduce incident time-to-recover.',
      'Improved reliability with unit tests and CI checks; contributed to Dockerized environments.',
    ],
    skills: [
      'Java',
      'Spring Boot',
      'Angular',
      'MySQL',
      'Redis',
      'RabbitMQ',
      'Grafana',
      'Docker',
      'CI/CD',
      'Unit Tests',
    ],
  },
  {
    period: '2017 – 2018',
    title: 'SOFTWARE DEVELOPER',
    company: 'Xhockware (YouBeep)',
    location: 'Porto, Portugal',
    summary:
      'Hybrid mobile app (Vue + Apache Cordova, Framework7) for in-store checkout.',
    impact: [
      'Reworked legacy views into reusable Vue components to improve maintainability.',
      'Improved navigation/state patterns and performance on low-end devices (lazy views, bundle trims).',
      'Implemented UI from design mocks and tested across multiple devices/resolutions.',
    ],
    skills: ['Vue', 'Cordova', 'Framework7', 'HTML', 'CSS', 'Responsive UI'],
  },
];

const ResumePage = () => {
  return (
    <div className="min-h-screen bg-orange-200 py-16 px-6" id="resume">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-black mb-6 flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 bg-teal-600 rounded-full"
            aria-hidden="true"
          />
          Resume
        </h1>

        <div className="flex justify-between items-center mb-10">
          <h2 className="text-xl font-semibold">Experience</h2>
          <a
            href="/cv_rita_neves.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-teal-600 text-white text-sm font-medium px-5 py-2 rounded-full"
          >
            DOWNLOAD CV
          </a>
        </div>

        <div className="space-y-8">
          {experiences.map((exp, index) => (
            <section
              key={index}
              className="bg-white rounded-md shadow-md p-6 text-gray-800"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-teal-700 font-bold text-sm">
                  {exp.period}
                </h3>
                <div className="text-xs text-gray-500">{exp.location}</div>
              </div>

              <p className="font-semibold uppercase text-sm mt-1">
                {exp.title}
              </p>
              <p className="text-xs text-gray-500">{exp.company}</p>

              {exp.summary && <p className="mt-3 text-sm">{exp.summary}</p>}

              {exp.impact?.length ? (
                <ul className="mt-3 list-disc pl-5 space-y-1 text-sm">
                  {exp.impact.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              ) : null}

              {exp.skills?.length ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {exp.skills.map((s) => (
                    <Badge key={s}>{s}</Badge>
                  ))}
                </div>
              ) : null}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResumePage;
